/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  Spinner,
  SvgIcon,
  Button,
  TextArea,
  Input,
  // FixedTextInput,
  Picklist
} from '../'

import {
  IDENTITY_PICSA_ADVISORY_SERVICES
} from '../../constants'

class EmailSend extends React.Component {
  static propTypes = {
    identity: PropTypes.string.isRequired,
    relation: PropTypes.shape({
      contentType: PropTypes.number,
      objectId: PropTypes.string
    }),
    onEmailCreateSuccess: PropTypes.func,
    onEmailCreateFail: PropTypes.func,
    onEmailRelationCreateSuccess: PropTypes.func,
    onEmailRelationCreateFail: PropTypes.func,
    onCancel: PropTypes.func,
    cancelButton: PropTypes.bool,
    replyToEmail: PropTypes.object,
    noRecordsRender: PropTypes.element,
    className: PropTypes.string
  }

  constructor(props) {
    super(props)

    this.state = {
      showCc: false,
      toAddrs: [],
      ccAddrs: [],
      bccAddrs: [],
      emailSubject: '',
      messageContent: ''
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.identity) {
      this.setDefaultEmailAddress(nextProps.data.identity)
    }

    if (nextProps.replyToEmail) {
      this.setState({
        emailSubject: `Re: ${nextProps.replyToEmail.subject}`
      })
    }
  }

  setDefaultEmailAddress = (identity) => {
    if (identity.activeEmailAddresses.edges.length > 0) {
      let defaultEmailAddressRecord = identity.activeEmailAddresses.edges.find(emailAddress => (
        emailAddress.node.default
      ))
      if (defaultEmailAddressRecord) {
        this.setState({
          toAddrs: Object.assign([], { 0: defaultEmailAddressRecord.node.addressValue })
        })
      }
    }
  }

  // TODO: add file upload/attach functionality
  sendEmail = (replyToEmail, attachments) => {
    let input = {
      subject: this.state.emailSubject,
      textBody: this.state.messageContent,
      // TODO: add toIdentities?  Should only supply one of ccAddrs or ccIdentities though
      // ccIdentities: ,
      trackOpens: true,
      fromIdentity: IDENTITY_PICSA_ADVISORY_SERVICES,
      direction: "outbound"
    }

    // TODO: uncomment below once we are ready for adding cc & bcc recipients
    // if (this.state.ccAddrs.length > 0) {
    //   input.ccAddrs = this.state.ccAddrs
    // }
    //
    // if (this.state.bccAddrs.length > 0) {
    //   input.bccAddrs = this.state.bccAddrs
    // }

    if (replyToEmail) {
      input.inReplyTo = replyToEmail.id
      input.toAddrs = [replyToEmail.fromAddr]
    } else {
      input.toAddrs = this.state.toAddrs
    }

    if (attachments) {
      input.attachments = attachments
    }

    this.props.createEmail({ variables: { input } })
    .then(({ data }) => {
      console.log("Email created successfully")
      if (this.props.onEmailCreateSuccess) {
        this.props.onEmailCreateSuccess(data.emailMutate.email.id)
      }

      this.setState({
        emailSubject: '',
        messageContent: '',
        messageSendSuccess: true
      })

      if (this.props.relation) {
        this.createEmailRelation(data.emailMutate.email.id)
      }
    })
    .catch((error) => {
      console.log("Email creation failed", error)
      if (this.props.onEmailCreateFail) {
        this.props.onEmailCreateFail()
      }
    })
  }

  createEmailRelation = (email) => {
    let input = {
      email,
      contentType: this.props.relation.contentType,
      objectId: this.props.relation.objectId
    }

    this.props.createEmailRelation({ variables: { input } })
    .then(({ data }) => {
      console.log("email relation created successfully")
      if (this.props.onEmailRelationCreateSuccess) {
        this.props.onEmailRelationCreateSuccess(data.emailrelationMutate.emailrelation.email.id)
      }
    })
    .catch((error) => {
      console.log("email relation creation failed", error)
      if (this.props.onEmailRelationCreateFail) {
        this.props.onEmailRelationCreateFail()
      }
    })
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className="slds-is-relative" size="small" />
    }

    if (this.props.data.error) {
      return (
        <div>
          <SvgIcon
            variant="utility"
            symbol="warning"
            colour="error"
            size="small"
            className="slds-m-left_small slds-m-right_small"
          />
          Fetching identity data failed.
          <Button
            type="reset"
            onClick={() => this.props.data.refetch()}
            className="slds-m-left_small">
            Try again
          </Button>
        </div>
      )
    }

    if (this.props.data.identity.activeEmailAddresses.edges.length === 0) {
      return (
        this.props.noRecordsRender || <span></span>
      )
    }

    return (
      <React.Fragment>
        {this.props.replyToEmail
          ? <span></span>
          : <div className="slds-box slds-m-bottom_small" style={{ padding: 0, backgroundColor: "white" }}>
              <div className="slds-grid">
                <div className="slds-m-left_x-small slds-m-top_xx-small slds-m-right_medium slds-text-color_weak">
                  To
                </div>
                <Picklist
                  id="toAddrs"
                  options={this.props.data.identity.activeEmailAddresses.edges.map(emailAddress => (
                    emailAddress.node.addressValue
                  ))}
                  value={this.state.toAddrs[0]}
                  onChange={toAddr => { // TODO: allow multiple selections (add & remove)?
                    this.setState((prevState) => ({
                      toAddrs: Object.assign(prevState.toAddrs, { 0: toAddr }) // TODO: add to back (.length) of toAddrs array instead of front
                    }))
                  }}
                  symbol="chevrondown"
                  closeDropdownOnSelect={true}  // TODO: will have to remove if/when allowing multilple selections
                  inline={true}
                  className="slds-col"
                />
                {/* TODO: uncomment below once we are ready for adding cc & bcc recipients */}
                {/* {this.state.showCc || this.state.showBcc
                  ? <span></span>
                  : <div className="slds-grid slds-m-right_small">
                      <Button
                        type="reset"
                        onClick={() => this.setState({ showCc: true })}>
                        Cc
                      </Button>
                      <Button
                        type="reset"
                        onClick={() => this.setState({ showBcc: true })}>
                        Bcc
                      </Button>
                    </div>
                } */}
              </div>
              {/* TODO: uncomment below once we are ready for adding cc & bcc recipients */}
              {/* {this.state.showCc
                ? <FixedTextInput
                    text="Cc"
                    value={this.state.ccAddrs}
                    onChange={(e) => {
                      let emailAddressInput = e.target.value
                      this.setState((prevState) => ({
                        ccAddrs: Object.assign(prevState.ccAddrs, { 0: emailAddressInput })
                      }))
                    }}
                    postText={
                      this.state.showBcc
                        ? <span></span>
                        : this.state.showBcc
                            ? null
                            : <Button
                                type="reset"
                                onClick={() => this.setState({ showBcc: true })}>
                                Bcc
                              </Button>
                    }
                    bare={true}
                  />
                : <span></span>
              }
              {this.state.showBcc
                ? <FixedTextInput
                    text="Bcc"
                    value={this.state.bccAddrs}
                    onChange={(e) => {
                      let emailAddressInput = e.target.value
                      this.setState((prevState) => ({
                        bccAddrs: Object.assign(prevState.bccAddrs, { 0: emailAddressInput })
                      }))
                    }}
                    postText={
                      this.state.showCc
                        ? <span></span>
                        : this.state.showCc
                            ? null
                            : <Button
                                type="reset"
                                onClick={() => this.setState({ showCc: true })}>
                                Cc
                              </Button>
                    }
                    bare={true}
                  />
                : <span></span>
              } */}
            </div>
        }
        {this.props.replyToEmail
          ? null
          : <Input
              placeholder="Subject"
              value={this.state.emailSubject}
              onChange={(e) => {
                let nextState = {
                  emailSubject: e.target.value
                }
                // TODO: uncomment below once we are ready for adding cc & bcc recipients
                // if (this.state.showCc && this.state.ccAddrs.length === 0) {
                //   nextState.showCc = false
                // }
                // if (this.state.showBcc && this.state.bccAddrs.length === 0) {
                //   nextState.showBcc = false
                // }
                this.setState(nextState)
              }}
              className="slds-m-bottom_small"
            />
        }
        <TextArea
          value={this.state.messageContent}
          onChange={(e) => this.setState({ messageContent: e.target.value })}
          className="fullwidth"
          rows={3}
        />
        <div className="slds-m-top_medium">
          {this.props.cancelButton
            ? <Button
                onClick={this.props.onCancel}
                minWidth={100}>
                Cancel
              </Button>
            : <span></span>
          }
          <Button
            type="brand"
            onClick={() => this.sendEmail(this.props.replyToEmail)}
            minWidth={100}
            className="slds-m-left_medium"
            disabled={this.state.messageContent.length === 0}>
            SEND
          </Button>
        </div>
      </React.Fragment>
    )
  }
}

const getIdentityActiveEmailAddresses = gql`
  query($id: ID!) {
    identity(id: $id) {
      id
      activeEmailAddresses: addresses(addressType: "email", optedOut: false, inactive: false) {
        edges {
          node {
            id
            addressValue
            default
          }
        }
      }
    }
  }
`

const createEmail = gql`
  mutation createEmail($input: EmailMutationInput!) {
    emailMutate(input: $input) {
      email {
        ...Email
      }
    }
  }
  ${fragments.email}
`

const createEmailRelation = gql`
  mutation createEmailRelation($input: EmailRelationMutationInput!) {
    emailrelationMutate(input: $input) {
      emailrelation {
        id
        email {
          id
        }
      }
    }
  }
`

const withData =
  graphql(getIdentityActiveEmailAddresses, {
    options: ({ identity }) => ({
      variables: {
        id: identity
      }
    })
  })

const withEmailMutate =
  graphql(createEmail, {
    name: "createEmail"
  })

const withEmailRelationMutate =
  graphql(createEmailRelation, {
    name: "createEmailRelation"
  })

export default compose(
  withData,
  withEmailMutate,
  withEmailRelationMutate
)(EmailSend)



// WEBPACK FOOTER //
// ./src/components/email-send/index.js